import L from 'leaflet'
import { STYLE_DEFINITIONS, MAP_SHAPE_ICONS } from '../icons.js'

/**
 * ShapeRenderer - Renders complex shapes with multiple geometries
 *
 * Handles rendering of shapes from the workflow server, including FreeDrawing
 * (custom symbols) and predefined styles (from STYLE_DEFINITIONS).
 */
export default class ShapeRenderer {
  /**
   * Create a new ShapeRenderer
   * @param {ItemManager} itemManager - The ItemManager instance
   * @param {LayerManager} layerManager - The LayerManager instance
   * @param {L.Map} leafletMap - The Leaflet map instance
   * @param {Object} [options={}] - Configuration options
   * @param {number} [options.iconsize=32] - Default icon size
   * @param {Function} [options.warnFn] - Optional warning function for logging
   */
  constructor(itemManager, layerManager, leafletMap, options = {}) {
    this._itemManager = itemManager
    this._layerManager = layerManager
    this._map = leafletMap
    this._iconsize = options.iconsize || 32
    this._warn = options.warnFn || console.warn
  }

  /**
   * Add a shape as a FeatureGroup (can contain multiple geometries)
   *
   * @param {string} id - Shape ID from server
   * @param {string} style - Style identifier ('FreeDrawing' or predefined style)
   * @param {Array<Object>} geometries - Array of geometry objects
   * @param {string} popupContent - HTML popup content for the entire shape
   * @returns {L.FeatureGroup|undefined} Leaflet feature group or undefined if failed
   */
  addShapeGroup(id, style, geometries, popupContent) {
    const layerId = 'shapes'
    const layer = this._layerManager._getLayer(layerId)

    if (!layer) {
      this._warn('Shapes layer not found')
      return
    }

    // Remove existing if updating
    if (layer.items[id]) {
      this._itemManager.removeItem(id)
    }

    // Create FeatureGroup to hold all geometries
    const featureGroup = L.featureGroup()

    geometries.forEach(geom => {
      let leafletElement

      switch (geom.type) {
        case 'Point':
          leafletElement = this._createPointGeometry(geom, style)
          break
        case 'LineString':
          leafletElement = this._createLineGeometry(geom, style)
          break
        case 'Polygon':
          leafletElement = this._createPolygonGeometry(geom, style)
          break
      }

      if (leafletElement) {
        featureGroup.addLayer(leafletElement)
      }
    })

    // Add popup to entire group
    if (popupContent) {
      featureGroup.bindPopup(popupContent)
    }

    // Set pane for z-index control
    featureGroup.options.pane = `layer-${layerId}`
    featureGroup.addTo(layer.group)

    // Store reference
    layer.items[id] = {
      data: { id, style, geometries, popup: popupContent },
      element: featureGroup,
    }

    return featureGroup
  }

  /**
   * Render or update a complete shape with all its geometries
   *
   * @param {Object} shape - Shape data from server
   * @param {string} shape.id - Unique shape identifier
   * @param {Object} shape.json - GeoJSON-like structure with points/lines/polygons
   * @param {string} [shape.popup_text] - HTML content for popup
   * @param {string} [shape.style] - Style identifier
   * @param {boolean} [pan=false] - If true, pan/zoom map to fit shape bounds
   */
  renderShape(shape, pan = false) {
    const { id, json, popup_text, style } = shape

    if (!json) {
      this._warn('Shape has no json:', id)
      return
    }

    // Collect all geometries
    const geometries = []

    // Process points
    if (json.points && Array.isArray(json.points)) {
      json.points.forEach(point => {
        if (point.geometry) {
          geometries.push({
            type: 'Point',
            coordinates: [point.geometry.y, point.geometry.x],
            symbol: point.symbol,
          })
        }
      })
    }

    // Process lines
    if (json.lines && Array.isArray(json.lines)) {
      json.lines.forEach(line => {
        if (line.geometry && line.geometry.paths && line.geometry.paths[0]) {
          const paths = line.geometry.paths[0]
          geometries.push({
            type: 'LineString',
            coordinates: paths.map(p => [p[1], p[0]]),
            symbol: line.symbol,
          })
        }
      })
    }

    // Process polygons
    if (json.polygons && Array.isArray(json.polygons)) {
      json.polygons.forEach(polygon => {
        if (polygon.geometry && polygon.geometry.rings && polygon.geometry.rings[0]) {
          const rings = polygon.geometry.rings[0]
          geometries.push({
            type: 'Polygon',
            coordinates: rings.map(p => [p[1], p[0]]),
            symbol: polygon.symbol,
          })
        }
      })
    }

    if (geometries.length === 0) {
      this._warn('Shape has no valid geometries:', id)
      return
    }

    // Add/update the shape as a FeatureGroup
    this.addShapeGroup(id, style, geometries, popup_text)

    // Pan to shape if requested
    if (pan && geometries.length > 0) {
      const allLatLngs = []

      geometries.forEach(geom => {
        if (geom.type === 'Point') {
          allLatLngs.push(L.latLng(geom.coordinates[0], geom.coordinates[1]))
        } else if (geom.type === 'LineString' || geom.type === 'Polygon') {
          geom.coordinates.forEach(coord => {
            allLatLngs.push(L.latLng(coord[0], coord[1]))
          })
        }
      })

      if (allLatLngs.length > 0) {
        if (allLatLngs.length === 1) {
          this._map.panTo(allLatLngs[0])
        } else {
          const bounds = L.latLngBounds(allLatLngs)
          this._map.fitBounds(bounds, { padding: [50, 50] })
        }
      }
    }
  }

  /**
   * Create a Point geometry for shape groups
   * @private
   */
  _createPointGeometry(geom, style) {
    const [lat, lng] = geom.coordinates

    let iconHtml
    const iconSize = [this._iconsize, this._iconsize]
    const iconAnchor = [this._iconsize / 2, this._iconsize / 2]

    if (style === 'FreeDrawing') {
      const symbol = geom.symbol
      if (!symbol) {
        this._warn('FreeDrawing point missing symbol definition')
        return null
      }

      const color = symbol.color
      const cssColor = color.length === 4
        ? `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] / 255})`
        : `rgb(${color[0]}, ${color[1]}, ${color[2]})`

      iconHtml = this._createMarkerSVG(symbol.style || 'circle', cssColor)
    } else {
      const styleDef = STYLE_DEFINITIONS[style]
      if (!styleDef || !styleDef.point) {
        this._warn('Unknown style or no point definition:', style)
        return null
      }

      const iconName = styleDef.point.icon
      iconHtml = MAP_SHAPE_ICONS[iconName]

      if (!iconHtml) {
        this._warn('Icon not found for style:', style, iconName)
        return null
      }
    }

    const icon = L.divIcon({
      html: iconHtml,
      iconSize,
      iconAnchor,
      className: 'map-shape-icon',
    })

    return L.marker([lat, lng], { icon })
  }

  /**
   * Create a LineString geometry for shape groups
   * @private
   */
  _createLineGeometry(geom, style) {
    let color, weight, opacity = 1

    if (style === 'FreeDrawing') {
      const symbol = geom.symbol
      if (!symbol) {
        this._warn('FreeDrawing line missing symbol definition')
        return null
      }

      const colorArray = symbol.color
      color = colorArray.length === 4
        ? `rgba(${colorArray[0]}, ${colorArray[1]}, ${colorArray[2]}, ${colorArray[3] / 255})`
        : `rgb(${colorArray[0]}, ${colorArray[1]}, ${colorArray[2]})`

      weight = symbol.width || 3
    } else {
      const styleDef = STYLE_DEFINITIONS[style]
      if (!styleDef || !styleDef.line) {
        this._warn('Unknown style or no line definition:', style)
        return null
      }

      color = styleDef.line.color
      weight = styleDef.line.weight
      opacity = styleDef.line.opacity || 1
    }

    return L.polyline(geom.coordinates, { color, weight, opacity })
  }

  /**
   * Create a Polygon geometry for shape groups
   * @private
   */
  _createPolygonGeometry(geom, style) {
    let color, weight, fillColor, fillOpacity

    if (style === 'FreeDrawing') {
      const symbol = geom.symbol
      if (!symbol) {
        this._warn('FreeDrawing polygon missing symbol definition')
        return null
      }

      const fillArray = symbol.color
      fillColor = fillArray.length === 4
        ? `rgba(${fillArray[0]}, ${fillArray[1]}, ${fillArray[2]}, ${fillArray[3] / 255})`
        : `rgb(${fillArray[0]}, ${fillArray[1]}, ${fillArray[2]})`
      fillOpacity = fillArray.length === 4 ? fillArray[3] / 255 : 0.5

      if (symbol.outline) {
        const outlineArray = symbol.outline.color
        color = outlineArray.length === 4
          ? `rgba(${outlineArray[0]}, ${outlineArray[1]}, ${outlineArray[2]}, ${outlineArray[3] / 255})`
          : `rgb(${outlineArray[0]}, ${outlineArray[1]}, ${outlineArray[2]})`
        weight = symbol.outline.width || 2
      } else {
        color = fillColor
        weight = 2
      }
    } else {
      const styleDef = STYLE_DEFINITIONS[style]
      if (!styleDef || !styleDef.polygon) {
        this._warn('Unknown style or no polygon definition:', style)
        return null
      }

      color = styleDef.polygon.color
      weight = styleDef.polygon.weight
      fillColor = styleDef.polygon.fillColor
      fillOpacity = styleDef.polygon.fillOpacity
    }

    return L.polygon(geom.coordinates, { color, weight, fillColor, fillOpacity })
  }

  /**
   * Create SVG marker for FreeDrawing points
   * @private
   */
  _createMarkerSVG(markerStyle, color) {
    const size = 16
    const center = size / 2

    switch (markerStyle) {
      case 'circle':
        return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${center}" cy="${center}" r="${center - 2}" fill="${color}" stroke="white" stroke-width="2"/>
        </svg>`

      case 'square':
        return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <rect x="2" y="2" width="${size - 4}" height="${size - 4}" fill="${color}" stroke="white" stroke-width="2"/>
        </svg>`

      case 'diamond':
        return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <polygon points="${center},2 ${size - 2},${center} ${center},${size - 2} 2,${center}" fill="${color}" stroke="white" stroke-width="2"/>
        </svg>`

      case 'cross':
        return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <line x1="${center}" y1="2" x2="${center}" y2="${size - 2}" stroke="${color}" stroke-width="3"/>
          <line x1="2" y1="${center}" x2="${size - 2}" y2="${center}" stroke="${color}" stroke-width="3"/>
        </svg>`

      case 'x':
        return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <line x1="2" y1="2" x2="${size - 2}" y2="${size - 2}" stroke="${color}" stroke-width="3"/>
          <line x1="${size - 2}" y1="2" x2="2" y2="${size - 2}" stroke="${color}" stroke-width="3"/>
        </svg>`

      default:
        return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${center}" cy="${center}" r="${center - 2}" fill="${color}"/>
        </svg>`
    }
  }
}
